import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  IconButton,
  Button,
  Snackbar
} from '@mui/material';
import { Refresh, Language, CheckCircle, Cancel } from '@mui/icons-material';

const STATUS_FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'new', label: 'Neu' },
  { value: 'approval', label: 'Zur Freigabe' },
  { value: 'approved', label: 'Freigegeben' },
  { value: 'rejected', label: 'Abgelehnt' },
  { value: 'uploaded', label: 'Hochgeladen' }
];

const STATUS_COLORS = {
  new: { bg: '#E3F2FD', fg: '#1565C0' },        // blue
  approval: { bg: '#FFF8E1', fg: '#F57F17' },   // amber - waiting for a decision
  approved: { bg: '#E8F5E9', fg: '#2E7D32' },   // green
  rejected: { bg: '#FFEBEE', fg: '#C62828' },   // red
  uploaded: { bg: '#F3E5F5', fg: '#6A1B9A' }    // purple - live on GMB
};

const MediaApprovalPage = () => {
  const { locationId } = useParams();
  const [images, setImages] = useState([]);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyImageId, setBusyImageId] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [failedPreviews, setFailedPreviews] = useState(() => new Set());

  const loadImages = async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/businesses/${locationId}/media-approval`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setImages(data.images || []);
      setBusinessName(data.businessName || '');
    } catch (err) {
      console.error('Failed to load media approval list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  // Approve or reject an image awaiting a decision, then reload the list so it
  // moves from the 'approval' tab into 'approved' or 'rejected'
  const decideImage = async (imageId, action) => {
    setBusyImageId(imageId);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/businesses/${locationId}/media-approval/${imageId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setSnackbar({
        severity: 'success',
        message: action === 'approve' ? '✅ Bild freigegeben' : '❌ Bild abgelehnt'
      });
      await loadImages();
    } catch (err) {
      console.error('Failed to update image status:', err);
      setSnackbar({ severity: 'error', message: `Fehler: ${err.message}` });
      await loadImages(); // reload anyway - the 409 case means someone else already decided
    } finally {
      setBusyImageId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: images.length };
    for (const img of images) {
      c[img.status] = (c[img.status] || 0) + 1;
    }
    return c;
  }, [images]);

  const filteredImages = useMemo(() => {
    if (statusFilter === 'all') return images;
    return images.filter(img => img.status === statusFilter);
  }, [images, statusFilter]);

  const formatDate = (d) => d ? new Date(d).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : null;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            🖼️ Media Approval
          </Typography>
          {businessName && (
            <Typography variant="body2" color="text.secondary">
              {businessName} · {images.length} Bilder in der Media-Bibliothek
            </Typography>
          )}
        </Box>
        <Tooltip title="Neu laden">
          <IconButton onClick={loadImages} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status filter chips */}
      <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap>
        {STATUS_FILTERS.map(f => (
          <Chip
            key={f.value}
            label={`${f.label} (${counts[f.value] || 0})`}
            onClick={() => setStatusFilter(f.value)}
            color={statusFilter === f.value ? 'primary' : 'default'}
            variant={statusFilter === f.value ? 'filled' : 'outlined'}
            sx={{ fontWeight: statusFilter === f.value ? 'bold' : 'normal' }}
          />
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Fehler beim Laden der Medien: {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : filteredImages.length === 0 ? (
        <Alert severity="info">
          {statusFilter === 'all'
            ? 'Keine Bilder in der Media-Bibliothek dieses Unternehmens.'
            : `Keine Bilder mit Status "${STATUS_FILTERS.find(f => f.value === statusFilter)?.label}".`}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredImages.map(img => {
            const colors = STATUS_COLORS[img.status] || { bg: '#ECEFF1', fg: '#455A64' };
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={img._id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {img.previewUrl && !failedPreviews.has(img._id) ? (
                    <CardMedia
                      component="img"
                      height="180"
                      image={img.previewUrl}
                      alt={img.name}
                      sx={{ objectFit: 'cover', backgroundColor: '#f5f5f5' }}
                      onError={() => setFailedPreviews(prev => new Set(prev).add(img._id))}
                    />
                  ) : (
                    <Box height={180} display="flex" alignItems="center" justifyContent="center" bgcolor="#f5f5f5">
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const source = img.cloudKey || img.name || '';
                          const ext = source.includes('.') ? source.split('.').pop().toLowerCase() : null;
                          return ext ? `Keine Vorschau - ${ext}` : 'Keine Vorschau';
                        })()}
                      </Typography>
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1, pb: '12px !important' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Tooltip title={img.name || ''}>
                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: '70%' }}>
                          {img.name || 'Unbenannt'}
                        </Typography>
                      </Tooltip>
                      <Chip
                        label={img.status}
                        size="small"
                        sx={{ backgroundColor: colors.bg, color: colors.fg, fontWeight: 'bold' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      📁 {img.folderName || '–'}{img.category ? ` · ${img.category}` : ''}
                      {img.scrapedFromWebsite && (
                        <Tooltip title="Von der Website gescraped">
                          <Language sx={{ fontSize: 14, verticalAlign: 'text-bottom', ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </Typography>
                    {formatDate(img.sentToSlackAt) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        📤 Slack: {formatDate(img.sentToSlackAt)}
                      </Typography>
                    )}
                    {formatDate(img.approvedAt) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ✅ Freigegeben: {formatDate(img.approvedAt)}
                      </Typography>
                    )}
                    {formatDate(img.rejectedAt) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ❌ Abgelehnt: {formatDate(img.rejectedAt)}
                      </Typography>
                    )}
                    {formatDate(img.uploadedAt) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ☁️ Hochgeladen: {formatDate(img.uploadedAt)}
                      </Typography>
                    )}
                    {img.status === 'approval' && (
                      <Stack direction="row" spacing={1} mt={1.5}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          disabled={busyImageId === img._id}
                          onClick={() => decideImage(img._id, 'approve')}
                          fullWidth
                        >
                          Freigeben
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          disabled={busyImageId === img._id}
                          onClick={() => decideImage(img._id, 'reject')}
                          fullWidth
                        >
                          Ablehnen
                        </Button>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'info'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaApprovalPage;
